import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
} from "@intelligems/ig-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EXPERIMENT_ROUTES } from "../../routes/routes";
import { setConfetti, setDialog } from "../../store/slices/global";
import { mpClickToggleCampaign, mpStartExperience } from "../../utils/mixpanel";
import { useConfiguration } from "../configurations/use-configuration";
import { experienceKeys, historyKeys } from "../queryKeys";
import { useStartShippingExperience } from "../shipping-actions/shipping-action";
import { ApiRequest } from "../types/api";

import { experienceAction } from "./experience-action";

export const useStartExperience = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  );
  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType & {
        pausedAtTs: number | null;
        name: string;
      }
    >
  >({
    mutationKey: ["startExperience"],
    mutationFn: async (data) => experienceAction(data),
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          toast.info("Starting experience...");
        } else {
          toast.error("Error starting experience");
        }
      }
    },
    onSuccess: async (data, payload) => {
      await queryClient.invalidateQueries({
        queryKey:
          data.experience?.category === "experiment"
            ? experienceKeys.experimentList
            : experienceKeys.personalizationList,
      });
      void queryClient.invalidateQueries({
        queryKey: historyKeys.one(payload.pathParams.experienceId),
      });
      await queryClient.invalidateQueries({
        queryKey: experienceKeys.one(payload.pathParams.experienceId),
      });

      dispatch(setConfetti(true));
      if (data.experience?.category === "experiment") {
        mpStartExperience({
          id: payload.pathParams.experienceId,
          name: payload.pathParams.name,
          pausedAtTs: payload.pathParams.pausedAtTs,
        });
      }

      if (
        shouldDuplicateProducts?.value &&
        data.experience?.testTypes?.hasTestPricing
      ) {
        navigate(
          `${EXPERIMENT_ROUTES.DuplicateProducts}/${payload.pathParams.experienceId}`,
          {
            state: {
              action: "start",
            },
          }
        );
      } else {
        if (data.experience?.category === "experiment") {
          navigate(EXPERIMENT_ROUTES.Experiences);
          dispatch(setDialog({ dialog: "startTest", open: true }));
        }
      }
    },
  });
};
